import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Send, AlertOctagon, ShieldBan, Clock, Gauge, UserX } from 'lucide-react';
import { fmtDate } from './StatusBadge';

function Metric({ icon: Icon, label, value, tone = '#00E5FF', testId, hint }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#041A3B] p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#5C7599]">
        <Icon className="w-4 h-4" style={{ color: tone }} />
        {label}
      </div>
      <div className="font-outfit text-3xl font-semibold mt-2" style={{ color: tone }}>{value}</div>
      {hint && <div className="text-xs text-[#5C7599] mt-1">{hint}</div>}
    </div>
  );
}

export function OverviewTab({ stats }) {
  if (!stats) return null;
  const s = stats.by_status;
  const prevented = Object.values(stats.prevented || {}).reduce((a, b) => a + b, 0);
  const rateTone = stats.bounce_rate >= 5 ? '#FF5252' : stats.bounce_rate >= 2 ? '#FF9100' : '#00E676';
  return (
    <div className="space-y-6" data-testid="email-overview-tab">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Metric icon={Send} label="Sent" value={s.sent} tone="#00E676" testId="metric-sent" />
        <Metric icon={AlertOctagon} label="Bounced" value={s.bounced} tone="#FF5252" testId="metric-bounced" />
        <Metric icon={Gauge} label="Bounce rate" value={`${stats.bounce_rate}%`} tone={rateTone} testId="metric-bounce-rate" hint="Keep under 2%" />
        <Metric icon={Clock} label="Queue depth" value={stats.queue_depth} testId="metric-queue-depth" hint={`${stats.rate_per_minute}/min limit`} />
        <Metric icon={ShieldBan} label="Suppressed" value={stats.suppressions.total} tone="#FF9100" testId="metric-suppressed" hint={`${prevented} sends prevented`} />
        <Metric icon={UserX} label="Unverified" value={stats.unverified_users} tone="#FFEA00" testId="metric-unverified" />
      </div>

      <Card className="bg-[#041A3B] border-white/10">
        <CardHeader><CardTitle className="text-white text-base">Delivery — last 14 days</CardTitle></CardHeader>
        <CardContent className="h-64" data-testid="email-series-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats.series}>
              <CartesianGrid stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#5C7599', fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fill: '#5C7599', fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: '#000B18', border: '1px solid #ffffff20' }} />
              <Area type="monotone" dataKey="sent" stroke="#00E676" fill="#00E67622" />
              <Area type="monotone" dataKey="bounced" stroke="#FF5252" fill="#FF525222" />
              <Area type="monotone" dataKey="failed" stroke="#FF9100" fill="#FF910022" />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="bg-[#041A3B] border-white/10">
          <CardHeader><CardTitle className="text-white text-base">Top failing templates ({stats.days}d)</CardTitle></CardHeader>
          <CardContent data-testid="top-failing-templates">
            {stats.top_failing_templates.length === 0 ? (
              <p className="text-sm text-[#5C7599]">No failures in this window.</p>
            ) : stats.top_failing_templates.map((t) => (
              <div key={t.template_key} className="flex justify-between py-1.5 border-b border-white/5 text-sm">
                <span className="text-[#A0B4CC] font-mono">{t.template_key}</span>
                <span className="text-[#FF5252] font-semibold">{t.count}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="bg-[#041A3B] border-white/10">
          <CardHeader><CardTitle className="text-white text-base">System status</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2" data-testid="email-system-status">
            <Row label="SMTP" ok={stats.smtp_enabled} text={stats.smtp_enabled ? 'Configured' : 'Not configured — emails fail fast'} />
            <Row label="Queue worker" ok={!!stats.worker.heartbeat} text={`Last tick ${fmtDate(stats.worker.heartbeat)}`} />
            <Row label="Bounce mailbox (IMAP)" ok={stats.imap_enabled} text={stats.imap_enabled ? `Last poll ${fmtDate(stats.bounce_poll.last_poll_at)}` : 'Disabled — only SMTP-time bounces detected'} />
            {Object.entries(stats.suppressions.by_reason).map(([k, v]) => (
              <div key={k} className="flex justify-between text-[#A0B4CC]"><span className="capitalize">{k.replace(/_/g, ' ')}</span><span>{v}</span></div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, ok, text }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-white">{label}</span>
      <span className={`text-xs ${ok ? 'text-[#00E676]' : 'text-[#FF9100]'}`}>{text}</span>
    </div>
  );
}
